import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PencilIcon, TrashIcon, EyeIcon, PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import api from '../../services/api';
import { PermissionGate, useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';

interface Customer {
  id: string;
  accountName: string;
  accountShortName: string;
}

interface Engagement {
  id: string;
  number: string;
  name: string;
  description: string | null;
  status: string;
  billingType: string;
  currency: string;
  startDate: string;
  endDate: string | null;
  totalBudget: number | null;
  usedBudget: number;
  Customer: Customer;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const statusOptions = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const billingTypeOptions = [
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'FIXED', label: 'Fixed' },
  { value: 'RETAINER', label: 'Retainer' },
  { value: 'MILESTONE', label: 'Milestone' },
];

const timeRangeOptions = [
  { value: 'preset:today', label: 'Today' },
  { value: 'preset:yesterday', label: 'Yesterday' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
];

const convertPresetToCustom = (timeRangeValue: string): string => {
  if (!timeRangeValue.startsWith('preset:')) return timeRangeValue;
  const preset = timeRangeValue.replace('preset:', '') as DurationPreset;
  const range = getDurationPresetRange(preset);
  if (range.from && range.to) return `custom:${range.from},${range.to}`;
  return timeRangeValue;
};

function parseOptionalTimeFilter(sp: URLSearchParams, key: string): string {
  const t = sp.get(key);
  if (!t || t === 'all') return '';
  return t;
}

function appendDateFilterParams(params: URLSearchParams, prefix: string, rangeValue: string): void {
  if (!rangeValue) return;
  const converted = convertPresetToCustom(rangeValue);
  if (converted.startsWith('custom:')) {
    const [, dates] = converted.split('custom:');
    const [from, to] = dates.split(',');
    if (from) params.append(`${prefix}From`, from);
    if (to) params.append(`${prefix}To`, to);
  } else {
    params.append(`${prefix}Range`, converted);
  }
}

function formatTimeFilterChipLabel(rangeValue: string): string {
  if (!rangeValue) return '';
  if (rangeValue.startsWith('preset:')) {
    const option = timeRangeOptions.find((t) => t.value === rangeValue);
    return option?.label || rangeValue;
  }
  const option = timeRangeOptions.find((t) => t.value === rangeValue);
  if (option) return option.label;
  if (rangeValue.startsWith('custom:')) {
    const [, dates] = rangeValue.split('custom:');
    const [from, to] = dates.split(',');
    if (from && to) {
      try {
        const formatDateForDisplay = (dateStr: string) => {
          const date = new Date(dateStr.trim());
          if (isNaN(date.getTime())) return dateStr;
          return date.toLocaleString();
        };
        return `${formatDateForDisplay(from)} - ${formatDateForDisplay(to)}`;
      } catch {
        return rangeValue;
      }
    }
  }
  return rangeValue;
}

const EngagementList: React.FC = () => {
  const { formatDate } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterNumber = searchParams.get('number') ?? '';
  const filterCustomer = searchParams.get('customer') ?? '';
  const filterStatus = searchParams.get('status') ?? '';
  const filterBillingType = searchParams.get('billingType') ?? '';
  const filterStartDate = useMemo(() => parseOptionalTimeFilter(searchParams, 'startDate'), [searchParams]);
  const filterEndDate = useMemo(() => parseOptionalTimeFilter(searchParams, 'endDate'), [searchParams]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { can, loading: permissionsLoading } = useDynamicPermissions();

  const canView = can('engagements', 'read');

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers/dropdown');
      return response.data;
    },
  });

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountName?: string; name?: string }) => ({
        value: c.id,
        label: c.accountName || c.name || 'Unknown',
      })) ?? [],
    [customersData]
  );

  const fetchEngagements = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
      });
      if (appliedSearch) params.append('search', appliedSearch);
      if (filterName) params.append('name', filterName);
      if (filterNumber) params.append('number', filterNumber);
      if (filterCustomer) params.append('customerId', filterCustomer);
      if (filterStatus) params.append('status', filterStatus);
      if (filterBillingType) params.append('billingType', filterBillingType);
      appendDateFilterParams(params, 'startDate', filterStartDate);
      appendDateFilterParams(params, 'endDate', filterEndDate);

      const response = await api.get(`/engagements?${params}`);
      setEngagements(response.data.engagements);
      setTotal(response.data.total);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch engagements';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pageSize, page, appliedSearch, filterName, filterNumber, filterCustomer, filterStatus, filterBillingType, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (permissionsLoading) return;
    if (canView) fetchEngagements();
  }, [canView, fetchEngagements, permissionsLoading]);

  const handleDelete = async (id: string, engagementName: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete engagement "${engagementName}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm',
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/engagements/${id}`);
              toast.success('Engagement deleted successfully');
              fetchEngagements();
            } catch (err: any) {
              const errorMessage = err.response?.data?.error || 'Failed to delete engagement';
              toast.error(errorMessage);
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm',
        },
      ],
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null });
    setPage(1);
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'engagement':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null });
                setPage(1);
              }}
            />
          );
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null });
                setPage(1);
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({ customer: typeof v === 'string' && v ? v : null });
                setPage(1);
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={statusOptions}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ status: typeof v === 'string' && v ? v : null });
                setPage(1);
              }}
            />
          );
        case 'startDate':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterStartDate || 'all'}
              placeholder="All Time"
              active={searchParams.has('startDate')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({ startDate: val && val !== 'all' ? val : null });
                setPage(1);
              }}
            />
          );
        case 'endDate':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterEndDate || 'all'}
              placeholder="All Time"
              active={searchParams.has('endDate')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({ endDate: val && val !== 'all' ? val : null });
                setPage(1);
              }}
            />
          );
        case 'billingType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={billingTypeOptions}
              value={filterBillingType}
              placeholder="All Billing Types"
              onChange={(v) => {
                patchSearchParams({ billingType: typeof v === 'string' && v ? v : null });
                setPage(1);
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterName,
      filterNumber,
      customerOptions,
      filterCustomer,
      filterStatus,
      filterStartDate,
      filterEndDate,
      filterBillingType,
      searchParams,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) chips.push({ id: 'search', label: 'Search', value: `"${appliedSearch}"`, color: 'blue' });
    if (filterName) chips.push({ id: 'name', label: 'Engagement', value: filterName, color: 'blue' });
    if (filterNumber) chips.push({ id: 'number', label: 'Number', value: filterNumber, color: 'blue' });
    if (filterCustomer) {
      const customer = customerOptions.find((c: { value: string; label: string }) => c.value === filterCustomer);
      chips.push({ id: 'customer', label: 'Customer', value: customer?.label || 'Unknown', color: 'teal' });
    }
    if (filterStatus) {
      const statusLabel = statusOptions.find((s) => s.value === filterStatus)?.label || filterStatus;
      chips.push({ id: 'status', label: 'Status', value: statusLabel, color: 'purple' });
    }
    if (filterStartDate) {
      chips.push({ id: 'startDate', label: 'Start Date', value: formatTimeFilterChipLabel(filterStartDate), color: 'orange' });
    }
    if (filterEndDate) {
      chips.push({ id: 'endDate', label: 'End Date', value: formatTimeFilterChipLabel(filterEndDate), color: 'orange' });
    }
    if (filterBillingType) {
      const billingLabel = billingTypeOptions.find((b) => b.value === filterBillingType)?.label || filterBillingType;
      chips.push({ id: 'billingType', label: 'Billing', value: billingLabel, color: 'cyan' });
    }
    return chips;
  }, [appliedSearch, filterName, filterNumber, filterCustomer, filterStatus, filterStartDate, filterEndDate, filterBillingType, customerOptions]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null });
        break;
      case 'name':
        patchSearchParams({ name: null });
        break;
      case 'number':
        patchSearchParams({ number: null });
        break;
      case 'customer':
        patchSearchParams({ customer: null });
        break;
      case 'status':
        patchSearchParams({ status: null });
        break;
      case 'startDate':
        patchSearchParams({ startDate: null });
        break;
      case 'endDate':
        patchSearchParams({ endDate: null });
        break;
      case 'billingType':
        patchSearchParams({ billingType: null });
        break;
    }
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      number: null,
      customer: null,
      status: null,
      startDate: null,
      endDate: null,
      billingType: null,
    });
    setPage(1);
  };

  const formatCurrency = (amount: number | null, currency: string) => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'on_hold': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Engagements</h1>
          <p className="mt-2 text-sm text-gray-700">Manage customer engagements, resources, and billing.</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <PermissionGate resource="engagements" action="create">
            <Link to="/engagements/create" className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto">
              <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
              Create Engagement
            </Link>
          </PermissionGate>
        </div>
      </div>

      <div className="mt-6 bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSearch} className="sm:flex sm:items-center sm:space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search engagements..."
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  if (value === '') {
                    patchSearchParams({ search: null });
                    setPage(1);
                  }
                }}
              />
            </div>
            <button type="submit" className="mt-3 w-full inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto">
              Search
            </button>
          </form>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="mt-4 bg-white shadow rounded-lg px-4 py-2">
          <ActiveFilterChips chips={activeFilterChips} onRemove={handleRemoveFilterChip} onClearAll={handleClearAllFilters} />
        </div>
      )}

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('engagement', 'Engagement')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('number', 'Number')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('customer', 'Customer')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('status', 'Status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Budget</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('startDate', 'Start Date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('endDate', 'End Date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('billingType', 'Billing')}
                    </th>
                    <th className="relative px-6 py-3 bg-gray-50">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {engagements.map((engagement) => (
                    <tr key={engagement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{engagement.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{engagement.number}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{engagement.Customer?.accountName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{engagement.Customer?.accountShortName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(engagement.status)}`}>
                            {engagement.status.replace('_', ' ')}
                          </span>
                          {engagement.isActive === false && (
                            <span className="inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>Total: {formatCurrency(engagement.totalBudget, engagement.currency)}</div>
                        <div className="text-gray-500">Used: {formatCurrency(engagement.usedBudget, engagement.currency)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {engagement.startDate ? formatDate(engagement.startDate) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {engagement.endDate ? formatDate(engagement.endDate) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {engagement.billingType.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link to={`/engagements/${engagement.id}`} className="text-blue-600 hover:text-blue-900" title="View Details">
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          <PermissionGate resource="engagements" action="update">
                            <Link to={`/engagements/${engagement.id}/edit`} className="text-yellow-600 hover:text-yellow-900" title="Edit Engagement">
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          </PermissionGate>
                          <PermissionGate resource="engagements" action="delete">
                            <button onClick={() => handleDelete(engagement.id, engagement.name)} className="text-red-600 hover:text-red-900" title="Delete Engagement">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size, index) => {
                      const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                      const isDisabled = previousSize >= total && size > total;
                      return (
                        <option key={size} value={size} disabled={isDisabled}>{size}</option>
                      );
                    })}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>
                <div className="text-sm text-gray-700">
                  Showing {Math.min((page - 1) * pageSize + 1, total)} to {Math.min(page * pageSize, total)} of {total} entries
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="First page">&laquo;</button>
                  <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                  {(() => {
                    const pages: (number | string)[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else if (page <= 4) {
                      for (let i = 1; i <= 5; i++) pages.push(i);
                      pages.push('...', totalPages);
                    } else if (page >= totalPages - 3) {
                      pages.push(1, '...');
                      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                    }
                    return pages.map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-3 py-1 text-sm text-gray-500">...</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className={`px-3 py-1 text-sm border rounded-md ${page === p ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50'}`}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}
                  <button onClick={() => setPage(page + 1)} disabled={page >= totalPages} className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                  <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Last page">&raquo;</button>
                </div>
              </div>
            )}

            {engagements.length === 0 && !loading && (
              <div className="text-center py-12">
                <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No engagements</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating a new engagement.</p>
                <div className="mt-6">
                  <PermissionGate resource="engagements" action="create">
                    <Link to="/engagements/create" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                      Create Engagement
                    </Link>
                  </PermissionGate>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EngagementList;
